import React, { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { DescriptionList, LinkAsButton, Transition } from '@printdeal/ui-library';
import { useExperiment } from '@printdeal/feature-management/react';

import { SanitizedLineItem } from '../../../../../types/order/Order';
import OrderDetailContext from '../../contexts/OrderDetailContext';
import { dispatchAccountEvent } from '../../../../../helpers/dataLayerHelpers/accountDataLayerHelper';
import ArtServiceDescription from './ArtServiceDescription';

interface LineItemDescriptionProps {
  lineItem: SanitizedLineItem;
}

interface IDescriptionItem {
  attributeName: string;
  attributeValue: string | number;
}

const LineItemDescription = ({
  lineItem,
}: LineItemDescriptionProps) => {
  const exp1521 = useExperiment('hammerheads_exp1521_show_all_product_attributes');
  const attributeLimit = exp1521.experiment.variation === 'variant' ? 999 : 4;
  const { sane: { productDescription, countProductDescription } } = lineItem;

  // Load the order data from the context
  const { order } = useContext(OrderDetailContext);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);

  // Abort if no order data is available yet
  if (!order) return null;

  const toggleFullDescription = () => {
    setIsDescriptionOpen(!isDescriptionOpen);
    dispatchAccountEvent('order_detail_interaction', 'show_product_details');
  };

  return (
    <>
      <div className="pd-mb-2">
        <ArtServiceDescription lineItem={lineItem} />
        <DescriptionList className="md:pd-grid-cols-3">
          {productDescription.map((descriptionItem: IDescriptionItem, index: number) => (index < attributeLimit && (
            <React.Fragment key={descriptionItem.attributeName}>
              <DescriptionList.Title data-cy-item-description-attribute-name={descriptionItem.attributeName}>
                {`${descriptionItem.attributeName}: `}
              </DescriptionList.Title>
              <DescriptionList.Content
                data-cy-item-description-attribute-value={descriptionItem.attributeValue}
                className="md:pd-col-span-2"
              >
                {descriptionItem.attributeValue}
              </DescriptionList.Content>
            </React.Fragment>
          )))}
        </DescriptionList>
      </div>
      {
        countProductDescription >= attributeLimit && (
          <>
            <LinkAsButton
              variant="secondary"
              type="button"
              onClick={toggleFullDescription}
              className="pd-text-xs"
              data-cy="showMoreButton"
              iconBefore={isDescriptionOpen ? 'double-chevron-up' : 'double-chevron-down'}
            >
              {isDescriptionOpen ? (
                <FormattedMessage id="lineItems.list.hideFullDescription" defaultMessage="Show less" />
              ) : (
                <FormattedMessage id="lineItems.list.showFullDescription" defaultMessage="Show more" />
              )}
            </LinkAsButton>
            <Transition show={isDescriptionOpen} variant="appear-from-top">
              <div className="pd-mt-2">
                <DescriptionList className="md:pd-grid-cols-3">
                  {productDescription
                    .map((descriptionItem: IDescriptionItem, index: number) => (index >= attributeLimit && (
                    <React.Fragment key={descriptionItem.attributeName}>
                      <DescriptionList.Title data-cy-item-description-attribute-name={descriptionItem.attributeName}>
                        {`${descriptionItem.attributeName}: `}
                      </DescriptionList.Title>
                      <DescriptionList.Content
                        data-cy-item-description-attribute-value={descriptionItem.attributeValue}
                        className="md:pd-col-span-2"
                      >
                        {descriptionItem.attributeValue}
                      </DescriptionList.Content>
                    </React.Fragment>
                    )))}
                </DescriptionList>
              </div>
            </Transition>
          </>
        )
      }
    </>
  );
};

export default LineItemDescription;
